import {
  ACESFilmicToneMapping,
  BasicShadowMap,
  PCFSoftShadowMap,
  SRGBColorSpace,
  WebGLRenderer,
} from 'three';

let renderer = null;
let mount = null;
let pixelRatio = 1;
let resizeCallback = null;

function readSize() {
  return {
    width: window.innerWidth || 1,
    height: window.innerHeight || 1,
  };
}

export function initRenderer(options = {}) {
  const {
    mount: targetMount = document.getElementById('canvasMount'),
    pixelRatio: initialPixelRatio = window.devicePixelRatio || 1,
    onResize = null,
  } = options;

  if (!targetMount) {
    throw new Error('[renderer] Missing #canvasMount element.');
  }

  mount = targetMount;
  resizeCallback = onResize;
  pixelRatio = clampPixelRatio(initialPixelRatio);

  renderer = new WebGLRenderer({
    antialias: false,
    alpha: false,
    powerPreference: 'high-performance',
  });
  renderer.setClearColor(0x03060f, 1);
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.2;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = PCFSoftShadowMap;
  renderer.domElement.id = 'gameCanvas';
  renderer.domElement.style.display = 'block';

  mount.appendChild(renderer.domElement);
  resizeRenderer();
  window.addEventListener('resize', resizeRenderer);

  return renderer;
}

export function getRenderer() {
  if (!renderer) {
    throw new Error('[renderer] initRenderer() must run before getRenderer().');
  }
  return renderer;
}

export function setRendererPixelRatio(nextPixelRatio) {
  pixelRatio = clampPixelRatio(nextPixelRatio);
  resizeRenderer();
}

export function setRendererQuality({ shadows = false, softShadows = false, exposure = 1.04 } = {}) {
  const activeRenderer = getRenderer();
  activeRenderer.shadowMap.enabled = shadows;
  activeRenderer.shadowMap.type = softShadows ? PCFSoftShadowMap : BasicShadowMap;
  activeRenderer.toneMappingExposure = exposure;
}

function clampPixelRatio(value) {
  return Math.min(2, Math.max(0.5, Number.isFinite(value) ? value : 1));
}

export function resizeRenderer() {
  if (!renderer) return;

  const { width, height } = readSize();
  renderer.setPixelRatio(pixelRatio);
  renderer.setSize(width, height, false);

  if (resizeCallback) {
    resizeCallback({ width, height, pixelRatio, renderer });
  }
}

export function disposeRenderer() {
  if (!renderer) return;

  window.removeEventListener('resize', resizeRenderer);
  renderer.dispose();
  renderer.domElement.remove();
  renderer = null;
  mount = null;
  resizeCallback = null;
}
